import React, { type FormEvent, useCallback, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { Modal } from '../../../components/Modal/Modal'
import { type CustomIntegration } from '../../../models/CustomIntegration'
import { type ConnectCustomIntegrationPayload, useConnectCustomIntegrationMutation } from '../../../store/user/user'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { addToast } from '../../../store/toast/toastSlice'
import { type ErrorResponse } from '../../../common/errorMessages'

interface ConnectCredentialModalProps {
  show: boolean
  customIntegration: CustomIntegration
  onHide: () => void
}

interface CredentialFormData {
  secret: string
  username: string
}

const EMPTY_FORM: CredentialFormData = { secret: '', username: '' }

const SECRET_LABELS: Record<string, string> = {
  apiKey: 'API key',
  bearer: 'Bearer token',
  basic: 'Password',
}

const isBlank = (value: string): boolean => value.trim() === ''

const buildPayload = (authType: string, form: CredentialFormData): ConnectCustomIntegrationPayload | null => {
  if (isBlank(form.secret)) {
    return null
  }
  if (authType === 'apiKey') {
    return { connectType: 'api_key', apiKey: form.secret }
  }
  if (authType === 'bearer') {
    return { connectType: 'bearer', token: form.secret }
  }
  if (authType === 'basic') {
    return isBlank(form.username) ? null : { connectType: 'username_password', username: form.username, password: form.secret }
  }
  return null
}

const errorMessageOf = (error: unknown): string | null => {
  const data = (error as { data?: Partial<ErrorResponse> } | null)?.data
  const message = data?.errors?.[0]?.message
  return typeof message === 'string' && message !== '' ? message : null
}

export const ConnectCredentialModal: React.FC<ConnectCredentialModalProps> = ({ show, customIntegration, onHide }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const [connectCustomIntegration, { isLoading }] = useConnectCustomIntegrationMutation()
  const [form, setForm] = useState<CredentialFormData>(EMPTY_FORM)
  const { id, name, auth } = customIntegration
  const isBasic = auth.type === 'basic'
  const payload = buildPayload(auth.type, form)

  const handleHide = useCallback(() => {
    setForm(EMPTY_FORM)
    onHide()
  }, [onHide])

  const handleConnect = useCallback(async () => {
    if (!payload) {
      return
    }
    try {
      await connectCustomIntegration({ accountId, customIntegrationId: id, payload }).unwrap()
      dispatch(addToast({ variant: 'success', title: 'MCP server connected.' }))
      handleHide()
    } catch (error) {
      dispatch(
        addToast({
          variant: 'error',
          title: 'Failed to connect the MCP server',
          messages: [errorMessageOf(error) ?? 'Please check the credential and try again.'],
          delay: 10000,
        }),
      )
    }
  }, [payload, connectCustomIntegration, accountId, id, dispatch, handleHide])

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      await handleConnect()
    },
    [handleConnect],
  )

  return (
    <Modal
      show={show}
      onHide={handleHide}
      header={`Connect to ${name}`}
      body={
        <Form className='d-flex flex-column gap-3' onSubmit={handleSubmit}>
          <p className='mb-0 zocks-text-gray-600'>
            The credential is stored encrypted and only used for your own connection to {name}.
            {auth.type === 'apiKey' ? ` It is sent in the ${auth.headerName} header.` : ''}
          </p>
          {isBasic ? (
            <Form.Group controlId='connect-custom-integration-username'>
              <Form.Label className='paragraph-small mb-1'>Username</Form.Label>
              <Form.Control type='text' autoComplete='off' value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
            </Form.Group>
          ) : null}
          <Form.Group controlId='connect-custom-integration-secret'>
            <Form.Label className='paragraph-small mb-1'>{SECRET_LABELS[auth.type] ?? 'Credential'}</Form.Label>
            <Form.Control type='password' autoComplete='off' value={form.secret} onChange={(e) => setForm({ ...form, secret: e.target.value })} />
          </Form.Group>
        </Form>
      }
      footer={
        <div className='d-flex gap-3'>
          <Button variant='secondary' onClick={handleHide} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant='primary' disabled={!payload} loading={isLoading} onClick={handleConnect}>
            Connect
          </Button>
        </div>
      }
    />
  )
}
